import React, { Fragment, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Alert } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import FilePreview from "../Common/FilePreview";
import api from "../../../api";
import swal from "sweetalert";
import Select from "react-select";
import SignatureModal from "../Common/SignatureModal";

const DeliveryAdd = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showSignModal, setShowSignModal] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [error, setError] = useState("");
    const [units, setUnits] = useState([]);
    const [searchParams] = useSearchParams();

    const projectFromUrl = searchParams.get("project");
    const workOrderFromUrl = searchParams.get("workOrder");

    const [form, setForm] = useState({
        workOrder: "",
        purchaseOrder: [],
        project: "",
        vendor: "",
        challanDocument: null,
        items: [
            {
                itemName: "",
                quantityOrdered: "",
                quantityDelivered: "",
                unit: "",
                remark: "",
            },
        ],
        deliveryDate: new Date().toISOString().split("T")[0],
        clientStamp: null,
        testCertificate: null,
        status: "Pending",
        remarks: "",
        manualShipTo: { phone: "" },
        // Driver details
        driverName: "",
        driverPhone: "",
        vehicleNumber: "",
        transporterName: "",
    });

    const statuses = ["Pending", "Inspected"];

    // ------------------ INITIAL LOAD ------------------
    useEffect(() => {
        const init = async () => {
            setFetchLoading(true);
            setError("");
            try {
                await Promise.all([
                    fetchProjects(),
                    fetchPurchaseOrders(),
                    fetchVendors(),
                    fetchWorkOrders(),
                    fetchUnits(),
                ]);
            } catch (err) {
                console.error("Error initializing delivery form:", err);
                setError("Failed to load required data. Please refresh the page.");
            } finally {
                setFetchLoading(false);
            }
        };

        init();
    }, []);

    useEffect(() => {
        if (projectFromUrl) {
            setForm((prev) => ({
                ...prev,
                project: projectFromUrl,
            }));
        }
        if (workOrderFromUrl && workOrders.length > 0) {
            const matchedWO = workOrders.find((w) => w._id === workOrderFromUrl);
            const pId = matchedWO?.project?._id || matchedWO?.project || projectFromUrl || "";
            setForm((prev) => ({
                ...prev,
                workOrder: workOrderFromUrl,
                project: pId || prev.project,
            }));
        }
    }, [workOrderFromUrl, projectFromUrl, workOrders]);

    const fetchProjects = async () => {
        try {
            const response = await api.get("/projects?limit=1000");
            if (response.data.success) {
                setProjects(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            throw new Error("Could not fetch projects");
        }
    };

    const fetchPurchaseOrders = async () => {
        try {
            const response = await api.get("/purchase-orders?limit=1000");
            if (response.data.success) {
                setPurchaseOrders(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching purchase orders:", error);
            throw new Error("Could not fetch purchase orders");
        }
    };

    const fetchVendors = async () => {
        try {
            const response = await api.get("/vendors?limit=100");
            if (response.data.success) {
                setVendors(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching vendors:", error);
            throw new Error("Could not fetch vendors");
        }
    };

    const fetchWorkOrders = async () => {
        try {
            const response = await api.get("/work-orders?limit=1000");
            if (response.data.success) {
                setWorkOrders(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching work orders:", error);
            throw new Error("Could not fetch work orders");
        }
    };

    const blankItem = (poId = "", poNo = "") => ({
        itemName: "",
        quantityOrdered: "",
        quantityDelivered: "",
        unit: "",
        remark: "",
        purchaseOrder: poId,
        poNumber: poNo,
    });

    const filteredWorkOrders = form.project
        ? workOrders.filter((wo) => {
            const pId = wo.project?._id || wo.project;
            return String(pId) === String(form.project);
        })
        : workOrders;

    const filteredPurchaseOrders = form.workOrder
        ? purchaseOrders.filter((po) => {
            const woId = po.workOrder?._id || po.workOrder;
            return String(woId) === String(form.workOrder);
        })
        : [];

    // ------------------ MULTI PO CHANGE HANDLER ------------------
    const handlePurchaseOrderChange = (selectedOptions) => {
        const selectedPoIds = (selectedOptions || []).map((opt) => opt.value);

        if (!selectedPoIds.length) {
            setForm((prev) => ({
                ...prev,
                purchaseOrder: [],
                vendor: "",
                items: [blankItem()],
            }));
            return;
        }

        const selectedPOs = purchaseOrders.filter((po) =>
            selectedPoIds.includes(po._id)
        );

        // Derive vendor & project from first selected PO
        const firstPO = selectedPOs[0];
        const vendorId = firstPO?.vendor?._id || firstPO?.vendor || "";
        const projectId = firstPO?.project?._id || firstPO?.project || form.project;

        // Preserve items that belong to currently selected POs
        const existingValidItems = form.items.filter((item) =>
            item.purchaseOrder && selectedPoIds.includes(item.purchaseOrder)
        );

        // Find POs that were newly selected
        const currentSelectedPoIds = Array.isArray(form.purchaseOrder)
            ? form.purchaseOrder
            : (form.purchaseOrder ? [form.purchaseOrder] : []);

        const newlyAddedPoIds = selectedPoIds.filter(
            (id) => !currentSelectedPoIds.includes(id)
        );

        const newItems = [];
        newlyAddedPoIds.forEach((poId) => {
            const po = purchaseOrders.find((p) => p._id === poId);
            if (po && Array.isArray(po.items)) {
                po.items.forEach((i) => {
                    newItems.push({
                        itemName: i.itemName || "",
                        quantityOrdered: i.quantity || 0,
                        quantityDelivered: "",
                        unit: i.unit || "",
                        remark: "",
                        isNonBillable: i.isNonBillable || false,
                        subItems: (i.subItems || []).map((s) => ({
                            name: s.name || "",
                            quantityOrdered: s.quantity || 0,
                            quantity: s.quantity || 0,
                            quantityDelivered: s.quantity !== undefined ? s.quantity : "",
                            unit: s.unit || i.unit || "",
                            remarks: s.remarks || "",
                        })),
                        purchaseOrder: po._id,
                        poNumber: po.poNumber || "",
                    });
                });
            }
        });

        const combined = [...existingValidItems, ...newItems];

        setForm((prev) => ({
            ...prev,
            purchaseOrder: selectedPoIds,
            project: projectId || prev.project,
            vendor: vendorId || prev.vendor,
            items: combined.length > 0 ? combined : [blankItem()],
        }));
    };

    // ------------------ FORM CHANGE HANDLER ------------------
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "project") {
            setForm((prev) => ({
                ...prev,
                project: value,
                workOrder: "",
                purchaseOrder: [],
                vendor: "",
                items: [blankItem()],
            }));
            return;
        }

        if (name === "workOrder") {
            const selectedWO = workOrders.find((w) => w._id === value);
            const projectId = selectedWO ? (selectedWO.project?._id || selectedWO.project) : form.project;

            setForm((prev) => ({
                ...prev,
                workOrder: value,
                project: projectId || prev.project,
                purchaseOrder: [],
                vendor: "",
                items: [blankItem()],
            }));
            return;
        }

        if (name === "driverPhone") {
            const sanitized = value.replace(/[^\d+ _-]/g, ""); // Allow digits, +, space, _, -
            if (sanitized.length <= 16) {
                setForm((prev) => ({ ...prev, [name]: sanitized }));
            }
            return;
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };
    const fetchUnits = async () => {
        try {
            const res = await api.get("/boq-unit-types");

            if (res.data.success) {
                setUnits(res.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch units", err);
            swal("Error", "Failed to fetch unit master", "error");
        }
    };
    const handleSubItemChange = (itemIndex, subIndex, field, value) => {
        const updatedItems = [...form.items];
        const currentItem = { ...updatedItems[itemIndex] };
        const subItems = [...(currentItem.subItems || [])];
        let finalValue = value;

        if (field === "quantityDelivered" && finalValue !== "") {
            const numVal = parseFloat(finalValue);
            const maxVal = parseFloat(subItems[subIndex]?.quantityOrdered ?? subItems[subIndex]?.quantity);
            if (!isNaN(numVal) && !isNaN(maxVal) && numVal > maxVal) {
                finalValue = maxVal;
            }
        }

        const updatedSub = {
            ...subItems[subIndex],
            [field]: finalValue,
        };

        subItems[subIndex] = updatedSub;
        currentItem.subItems = subItems;

        // Pro-rata parent quantityDelivered from sub-items
        const totalSubPoQty = subItems.reduce(
            (sum, s) => sum + (parseFloat(s.quantityOrdered ?? s.quantity) || 0),
            0
        );
        const currentTotalDelivered = subItems.reduce(
            (sum, s) => sum + (parseFloat(s.quantityDelivered) || 0),
            0
        );
        const parentOrderedQty = parseFloat(currentItem.quantityOrdered) || 0;

        if (parentOrderedQty > 0 && totalSubPoQty > 0) {
            const newParentDelivered = currentTotalDelivered === 0
                ? 0
                : parseFloat(((currentTotalDelivered / totalSubPoQty) * parentOrderedQty).toFixed(4));
            currentItem.quantityDelivered = newParentDelivered;
        }

        updatedItems[itemIndex] = currentItem;
        setForm((prev) => ({ ...prev, items: updatedItems }));
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...form.items];

        // Prevent delivered > ordered
        if (field === "quantityDelivered") {
            const ordered = Number(updatedItems[index].quantityOrdered || 0);
            const delivered = Number(value);

            if (delivered > ordered) {
                swal(
                    "Warning",
                    "Delivered quantity cannot exceed ordered quantity!",
                    "warning",
                );
                return; // stop update
            }
        }

        updatedItems[index][field] = value;
        setForm((prev) => ({ ...prev, items: updatedItems }));
    };

    const addItem = () => {
        setForm((prev) => ({
            ...prev,
            items: [...prev.items, blankItem()],
        }));
    };

    const removeItem = (index) => {
        if (form.items.length === 1) {
            swal("Warning", "At least one item is required", "warning");
            return;
        }
        const updatedItems = form.items.filter((_, i) => i !== index);
        setForm((prev) => ({ ...prev, items: updatedItems }));
    };

    const handleFile = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                swal("Warning", "File size should not exceed 5MB", "warning");
                return;
            }
            setForm((prev) => ({
                ...prev,
                [field]: file,
            }));
        }
    };

    const removeFile = (field) => {
        setForm((prev) => ({
            ...prev,
            [field]: null,
        }));
    };

    // ------------------ SUBMIT & SIGNATURE ------------------
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.workOrder) {
            swal("Warning", "Please select a work order", "warning");
            return;
        }

        const poList = Array.isArray(form.purchaseOrder)
            ? form.purchaseOrder
            : (form.purchaseOrder ? [form.purchaseOrder] : []);

        if (poList.length === 0) {
            swal("Warning", "Please select at least one purchase order", "warning");
            return;
        }
        if (!form.vendor) {
            swal("Warning", "Vendor is not selected", "warning");
            return;
        }

        if (form.items.some((item) => !item.itemName || item.quantityDelivered === "" || item.quantityDelivered === undefined || item.quantityDelivered === null)) {
            swal(
                "Warning",
                "Please fill in all item details (name + delivered qty)",
                "warning",
            );
            return;
        }

        // Open Signature Modal
        setShowSignModal(true);
    };

    const handleSaveSignature = async (signatureFile, _stamp, signRemarks) => {
        setShowSignModal(false);
        try {
            setLoading(true);
            setError("");

            const uploadedFiles = {};

            if (form.challanDocument && form.challanDocument instanceof File) {
                const formData = new FormData();
                formData.append("file", form.challanDocument);

                const response = await api.post("/deliveries/upload/single", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (response.data.success) {
                    uploadedFiles.challanDocument = {
                        fileName: response.data.fileName,
                        fileUrl: response.data.fileUrl,
                        filePath: response.data.filePath,
                    };
                }
            }

            if (form.clientStamp && form.clientStamp instanceof File) {
                const formData = new FormData();
                formData.append("file", form.clientStamp);

                const response = await api.post("/deliveries/upload/single", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (response.data.success) {
                    uploadedFiles.clientStamp = {
                        fileName: response.data.fileName,
                        fileUrl: response.data.fileUrl,
                        filePath: response.data.filePath,
                    };
                }
            }

            if (form.testCertificate && form.testCertificate instanceof File) {
                const formData = new FormData();
                formData.append("file", form.testCertificate);

                const response = await api.post("/deliveries/upload/single", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (response.data.success) {
                    uploadedFiles.testCertificate = {
                        fileName: response.data.fileName,
                        fileUrl: response.data.fileUrl,
                        filePath: response.data.filePath,
                    };
                }
            }

            if (signatureFile) {
                const formData = new FormData();
                formData.append("file", signatureFile);

                const response = await api.post("/deliveries/upload/single", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (response.data.success) {
                    uploadedFiles.signature = {
                        fileName: response.data.fileName,
                        fileUrl: response.data.fileUrl,
                        filePath: response.data.filePath,
                        signedAt: new Date(),
                    };
                }
            }

            const poList = Array.isArray(form.purchaseOrder)
                ? form.purchaseOrder
                : form.purchaseOrder
                ? [form.purchaseOrder]
                : [];

            const sanitizedPoList = poList
                .map((p) => (p && typeof p === "object" ? p.value || p._id : p))
                .filter(Boolean);

            const sanitizedItems = form.items.map((item) => {
                const itemPo =
                    item.purchaseOrder && typeof item.purchaseOrder === "object"
                        ? item.purchaseOrder._id || item.purchaseOrder.value
                        : item.purchaseOrder;

                const poToUse =
                    itemPo || (sanitizedPoList.length === 1 ? sanitizedPoList[0] : null);

                return {
                    ...item,
                    purchaseOrder: poToUse || null,
                    poNumber: item.poNumber || "",
                    subItems: (item.subItems || []).map((s) => {
                        const rawDel = s.quantityDelivered !== undefined && s.quantityDelivered !== null && s.quantityDelivered !== ""
                            ? parseFloat(s.quantityDelivered)
                            : (s.quantity !== undefined && s.quantity !== "" ? parseFloat(s.quantity) : 0);
                        const delQty = isNaN(rawDel) ? 0 : rawDel;
                        const ordQty = parseFloat(s.quantityOrdered !== undefined ? s.quantityOrdered : s.quantity) || 0;
                        return {
                            name: s.name || "",
                            quantityOrdered: ordQty,
                            quantity: delQty,
                            quantityDelivered: delQty,
                            unit: s.unit || item.unit || "",
                            remarks: s.remarks || "",
                        };
                    }),
                };
            });

            const combinedRemarks = [form.remarks, signRemarks].filter(Boolean).join(" | ");

            const deliveryData = {
                purchaseOrder: sanitizedPoList,
                workOrder: form.workOrder,
                project: form.project,
                vendor: form.vendor,
                items: sanitizedItems,
                deliveryDate: form.deliveryDate,
                status: form.status,
                remarks: combinedRemarks,
                manualShipTo: form.manualShipTo?.phone ? { phone: form.manualShipTo.phone } : null,
                driverName: form.driverName,
                driverPhone: form.driverPhone,
                vehicleNumber: form.vehicleNumber,
                transporterName: form.transporterName,
                ...uploadedFiles,
            };

            const response = await api.post("/deliveries", deliveryData);

            if (response.data.success) {
                swal("Success", "Delivery created successfully!", "success");

                navigate(
                    projectFromUrl || workOrderFromUrl
                        ? `/delivery?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""
                        }`
                        : "/delivery",
                );
            }
        } catch (error) {
            console.error("Error creating delivery:", error);
            console.error("Error details:", error.response?.data);

            const errorMessage =
                error.response?.data?.message || "Failed to create delivery";

            setError(errorMessage);
            swal("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    // ------------------ LOADING UI ------------------
    if (fetchLoading) {
        return (
            <Fragment>
                <PageTitle activeMenu="Add Delivery" motherMenu="Procurement" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <Card.Body className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading required data...</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
        </Fragment>
        );
    }
    return (
        <Fragment>
            <PageTitle activeMenu="Add Delivery" motherMenu="Procurement" />

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError("")}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                </Alert>
            )}

            {(!purchaseOrders.length || !vendors.length || !workOrders.length) && (
                <Alert variant="warning">
                    <i className="fas fa-info-circle me-2"></i>
                    Some required data is missing:
                    {!purchaseOrders.length && <div>- No purchase orders found</div>}
                    {!vendors.length && <div>- No vendors found</div>}
                    {!workOrders.length && <div>- No work orders/projects found</div>}
                </Alert>
            )}

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <Card.Title>Add Delivery</Card.Title>
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    navigate(
                                        projectFromUrl || workOrderFromUrl
                                            ? `/delivery?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${workOrderFromUrl
                                                ? `workOrder=${workOrderFromUrl}`
                                                : ""
                                            }`
                                            : "/delivery",
                                    )
                                }
                            >
                                <i className="fas fa-arrow-left me-2"></i>Back
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                {/* Basic Details */}
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Delivery Number <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value="Auto-generated"
                                                readOnly
                                                disabled
                                            />
                                            <Form.Text className="text-muted">
                                                Auto-generated from sequence (DEL00001, DEL00002, ...)
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Project <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="project"
                                                value={form.project}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Project</option>
                                                {projects.map((p) => (
                                                    <option key={p._id} value={p._id}>
                                                        {p.projectName || p.name || p.projectCode}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Work orders will be filtered by selected project
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Work Order <span className="text-danger">*</span>
                                            </Form.Label>

                                            <Form.Select
                                                name="workOrder"
                                                value={form.workOrder}
                                                onChange={handleChange}
                                                required
                                                disabled={!form.project || !filteredWorkOrders.length}
                                            >
                                                <option value="">
                                                    {!form.project
                                                        ? "Select Project first"
                                                        : filteredWorkOrders.length
                                                        ? "Select Work Order"
                                                        : "No work orders found for this project"}
                                                </option>

                                                {filteredWorkOrders.map((wo) => (
                                                    <option key={wo._id} value={wo._id}>
                                                        {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}
                                                    </option>
                                                ))}
                                            </Form.Select>

                                            <Form.Text className="text-muted">
                                                Purchase Orders will be filtered by selected work order
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Purchase Order <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                isMulti
                                                name="purchaseOrder"
                                                placeholder={
                                                    !form.workOrder
                                                        ? "Select Work Order first"
                                                        : filteredPurchaseOrders.length
                                                        ? "Select Purchase Order(s)..."
                                                        : "No POs for selected work order"
                                                }
                                                isDisabled={!form.workOrder || !filteredPurchaseOrders.length}
                                                options={filteredPurchaseOrders.map((po) => ({
                                                    value: po._id,
                                                    label: `${po.poNumber} ${po.vendor?.name ? `- ${po.vendor.name}` : ""}`,
                                                }))}
                                                value={
                                                    (Array.isArray(form.purchaseOrder)
                                                        ? form.purchaseOrder
                                                        : form.purchaseOrder
                                                        ? [form.purchaseOrder]
                                                        : []
                                                    )
                                                        .map((id) => {
                                                            const po = purchaseOrders.find((p) => p._id === id);
                                                            return po
                                                                ? {
                                                                    value: po._id,
                                                                    label: `${po.poNumber} ${po.vendor?.name ? `- ${po.vendor.name}` : ""}`,
                                                                }
                                                                : null;
                                                        })
                                                        .filter(Boolean)
                                                }
                                                onChange={handlePurchaseOrderChange}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: "38px",
                                                    }),
                                                }}
                                            />
                                            <Form.Text className="text-muted">
                                                Select one or more POs. Vendor & items will auto-populate.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Vendor <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="vendor"
                                                value={form.vendor}
                                                onChange={handleChange}
                                                required
                                                disabled
                                            >
                                                <option value="">
                                                    {vendors.length
                                                        ? "Select Vendor"
                                                        : "No vendors available"}
                                                </option>
                                                {vendors.map((vendor) => (
                                                    <option key={vendor._id} value={vendor._id}>
                                                        {vendor.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Auto-selected when you choose a purchase order. You can
                                                override if needed.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ship To Phone / Contact Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="shipToPhone"
                                                value={form.manualShipTo?.phone || ""}
                                                onChange={(e) => setForm((prev) => ({
                                                    ...prev,
                                                    manualShipTo: { ...(prev.manualShipTo || {}), phone: e.target.value }
                                                }))}
                                                placeholder="Enter Ship To phone (optional)"
                                            />
                                            <Form.Text className="text-muted">
                                                Destination / Recipient site contact number
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Delivery Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="deliveryDate"
                                                value={form.deliveryDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={form.status}
                                                onChange={handleChange}
                                            >
                                                {statuses.map((status, i) => (
                                                    <option key={i} value={status}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Items Section */}
                                <div className="mt-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>Items ({form.items.length})</h5>
                                        <Button variant="success" size="sm" onClick={addItem}>
                                            <i className="fas fa-plus me-2"></i>Add Item
                                        </Button>
                                    </div>

                                    {form.items.map((item, index) => (
                                        <Card key={index} className="mb-3 border">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <h6 className="mb-0">Item #{index + 1}</h6>
                                                        {item.poNumber && (
                                                            <span className="badge bg-light text-primary border" style={{ fontSize: "0.75rem" }}>
                                                                <i className="fas fa-file-invoice me-1"></i>{item.poNumber}
                                                            </span>
                                                        )}
                                                        {item.isNonBillable ? (
                                                            <span className="badge bg-warning text-dark" style={{ fontSize: "0.7rem" }}>Non-Billable</span>
                                                        ) : (
                                                            <span className="badge bg-success" style={{ fontSize: "0.7rem" }}>Billable</span>
                                                        )}
                                                    </div>
                                                    {form.items.length > 1 && (
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => removeItem(index)}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </Button>
                                                    )}
                                                </div>

                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>
                                                                Item Name <span className="text-danger">*</span>
                                                            </Form.Label>
                                                            <Select
                                                                placeholder="Search & select item..."
                                                                isClearable
                                                                options={
                                                                    (Array.isArray(form.purchaseOrder)
                                                                        ? form.purchaseOrder
                                                                        : form.purchaseOrder
                                                                        ? [form.purchaseOrder]
                                                                        : []
                                                                    ).flatMap((pId) => {
                                                                        const p = purchaseOrders.find((x) => x._id === pId);
                                                                        if (!p || !Array.isArray(p.items)) return [];
                                                                        return p.items.map((i) => ({
                                                                            value: `${p._id}:::${i.itemName}`,
                                                                            itemName: i.itemName,
                                                                            quantity: i.quantity || 0,
                                                                            unit: i.unit || "",
                                                                            poId: p._id,
                                                                            poNumber: p.poNumber || "",
                                                                            isNonBillable: i.isNonBillable || false,
                                                                            label: `${i.itemName} (${p.poNumber || "PO"}) — ${i.quantity} ${i.unit || ""}`,
                                                                        }));
                                                                    })
                                                                }
                                                                value={
                                                                    item.itemName
                                                                        ? {
                                                                            value: item.purchaseOrder ? `${item.purchaseOrder}:::${item.itemName}` : item.itemName,
                                                                            label: `${item.itemName}${item.poNumber ? ` (${item.poNumber})` : ""} — ${item.quantityOrdered || ""} ${item.unit || ""}`,
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(selected) => {
                                                                    if (selected) {
                                                                        handleItemChange(index, "itemName", selected.itemName);
                                                                        handleItemChange(index, "quantityOrdered", selected.quantity);
                                                                        handleItemChange(index, "unit", selected.unit || "");
                                                                        handleItemChange(index, "purchaseOrder", selected.poId);
                                                                        handleItemChange(index, "poNumber", selected.poNumber);
                                                                        handleItemChange(index, "isNonBillable", selected.isNonBillable);
                                                                        const mappedSubs = (selected.subItems || []).map((s) => ({
                                                                            name: s.name || "",
                                                                            quantityOrdered: s.quantity || 0,
                                                                            quantity: s.quantity || 0,
                                                                            quantityDelivered: s.quantity !== undefined ? s.quantity : "",
                                                                            unit: s.unit || selected.unit || "",
                                                                            remarks: s.remarks || "",
                                                                        }));
                                                                        handleItemChange(index, "subItems", mappedSubs);
                                                                    } else {
                                                                        handleItemChange(index, "itemName", "");
                                                                    }
                                                                }}
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: "38px",
                                                                    }),
                                                                }}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Uom</Form.Label>
                                                            <Form.Select
                                                                value={item.unit || ""}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        "unit",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            >
                                                                {/* Placeholder */}
                                                                <option value="">Select Uom</option>

                                                                {/* Show current unit from PO if not in master */}
                                                                {item.unit &&
                                                                    !units.some((u) => u.name === item.unit) && (
                                                                        <option value={item.unit}>
                                                                            {item.unit}
                                                                        </option>
                                                                    )}

                                                                {/* Unit Master */}
                                                                {units.map((u) => (
                                                                    <option key={u._id} value={u.name}>
                                                                        {u.name}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col md={4}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Quantity Ordered</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                value={item.quantityOrdered}
                                                                readOnly
                                                                disabled
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={4}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>
                                                                Quantity Delivered{" "}
                                                                <span className="text-danger">*</span>
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={item.quantityDelivered}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        "quantityDelivered",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="Enter delivered quantity"
                                                                required
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={4}>


                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Item Remark</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={item.remark || ""}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        "remark",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="Enter remark"
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                {item.subItems && item.subItems.length > 0 && (
                                                    <div className="mt-2 p-2 bg-light rounded border">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <small className="text-primary fw-bold">
                                                                <i className="fa fa-list-ul me-1"></i> Sub-Items Breakdown (Enter delivered quantity for sub-items):
                                                            </small>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-bordered bg-white mb-0" style={{ fontSize: "0.82rem" }}>
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th style={{ width: "35%" }}>Sub-Item Name</th>
                                                                        <th style={{ width: "15%" }} className="text-center">PO Quantity</th>
                                                                        <th style={{ width: "20%" }} className="text-center">Delivered Qty <span className="text-danger">*</span></th>
                                                                        <th style={{ width: "15%" }} className="text-center">UOM</th>
                                                                        <th style={{ width: "15%" }}>Remarks</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.subItems.map((sub, sIdx) => {
                                                                        const maxQty = sub.quantityOrdered !== undefined ? sub.quantityOrdered : sub.quantity;
                                                                        const delVal = sub.quantityDelivered !== undefined && sub.quantityDelivered !== null
                                                                            ? sub.quantityDelivered
                                                                            : (sub.quantity !== undefined && sub.quantity !== null ? sub.quantity : "");
                                                                        return (
                                                                            <tr key={sIdx}>
                                                                                <td className="align-middle"><strong>{sub.name}</strong></td>
                                                                                <td className="align-middle text-center font-w600 text-muted">{maxQty}</td>
                                                                                <td className="align-middle">
                                                                                    <Form.Control
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        min="0"
                                                                                        max={maxQty !== undefined ? maxQty : undefined}
                                                                                        size="sm"
                                                                                        placeholder="0.00"
                                                                                        value={delVal}
                                                                                        onChange={(e) => handleSubItemChange(index, sIdx, "quantityDelivered", e.target.value)}
                                                                                        style={{ fontSize: "0.82rem" }}
                                                                                        required
                                                                                    />
                                                                                </td>
                                                                                <td className="align-middle">
                                                                                    <Form.Select
                                                                                        size="sm"
                                                                                        value={sub.unit || item.unit || ""}
                                                                                        onChange={(e) => handleSubItemChange(index, sIdx, "unit", e.target.value)}
                                                                                        style={{ fontSize: "0.82rem" }}
                                                                                    >
                                                                                        <option value="">Select UOM</option>
                                                                                        {(sub.unit || item.unit) &&
                                                                                            !units.some((u) => u.name === (sub.unit || item.unit)) && (
                                                                                                <option value={sub.unit || item.unit}>
                                                                                                    {sub.unit || item.unit}
                                                                                                </option>
                                                                                            )}
                                                                                        {units.map((u) => (
                                                                                            <option key={u._id} value={u.name}>
                                                                                                {u.name}
                                                                                            </option>
                                                                                        ))}
                                                                                    </Form.Select>
                                                                                </td>
                                                                                <td className="align-middle">
                                                                                    <Form.Control
                                                                                        type="text"
                                                                                        size="sm"
                                                                                        placeholder="Remark"
                                                                                        value={sub.remarks || ""}
                                                                                        onChange={(e) => handleSubItemChange(index, sIdx, "remarks", e.target.value)}
                                                                                        style={{ fontSize: "0.82rem" }}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>

                                {/* Documents Section */}
                                <div className="mt-4">
                                    <h5 className="mb-3">Documents</h5>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Upload Challan Document</Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    onChange={(e) => handleFile(e, "challanDocument")}
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                />
                                                <FilePreview
                                                    file={form.challanDocument}
                                                    onRemove={() => removeFile("challanDocument")}
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Upload other related doc</Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    onChange={(e) => handleFile(e, "clientStamp")}
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                />
                                                <FilePreview
                                                    file={form.clientStamp}
                                                    onRemove={() => removeFile("clientStamp")}
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Upload Test Certificate</Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    onChange={(e) => handleFile(e, "testCertificate")}
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                />
                                                <FilePreview
                                                    file={form.testCertificate}
                                                    onRemove={() => removeFile("testCertificate")}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

            {/* Remarks */}
            <Form.Group className="mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="remarks"
                    value={form.remarks}
                    onChange={handleChange}
                    placeholder="Enter any additional remarks..."
                />
            </Form.Group>

            {/* Dispatch / Driver Details - at bottom before buttons */}
            <div className="mt-4">
                <h5 className="mb-3">Dispatch / Driver Details</h5>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Driver Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="driverName"
                                value={form.driverName}
                                onChange={handleChange}
                                placeholder="Enter driver name"
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Driver Phone</Form.Label>
                            <Form.Control
                                type="text"
                                name="driverPhone"
                                value={form.driverPhone}
                                onChange={handleChange}
                                placeholder="Enter driver contact number"
                                maxLength={16}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vehicle Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="vehicleNumber"
                                value={form.vehicleNumber}
                                onChange={handleChange}
                                placeholder="e.g. RJ14 XX 1234"
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Transporter / Agency</Form.Label>
                            <Form.Control
                                type="text"
                                name="transporterName"
                                value={form.transporterName}
                                onChange={handleChange}
                                placeholder="Enter transporter / agency name"
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            {/* Submit Buttons */}
            <div className="d-flex gap-2 mt-4">
                <Button
                    type="submit"
                    variant="primary"
                    disabled={
                        loading ||
                        !purchaseOrders.length ||
                        !vendors.length ||
                        !workOrders.length
                    }
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Creating...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save me-2"></i>Create Delivery
                        </>
                    )}
                </Button>
                <Button
                    type="button"
                    variant="danger"
                    onClick={() =>
                        navigate(
                            projectFromUrl || workOrderFromUrl
                                ? `/delivery?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${workOrderFromUrl
                                    ? `workOrder=${workOrderFromUrl}`
                                    : ""
                                }`
                                : "/delivery",
                        )
                    }
                    disabled={loading}
                >
                    <i className="fas fa-times me-2"></i>Cancel
                </Button>
            </div>
        </Form>
			</Card.Body>
		</Card>
	</Col>
</Row>
            <SignatureModal
                show={showSignModal}
                onHide={() => {
                    setShowSignModal(false);
                    setLoading(false);
                }}
                onSave={handleSaveSignature}
                title="Authorised Signatory Signature for Delivery Challan"
            />
</Fragment>
);
};

export default DeliveryAdd;
